import React, { useMemo, useCallback, useState } from 'react';
import { Text, View, Pressable } from 'react-native';
import { useUnistyles } from 'react-native-unistyles';
import { StyleSheet } from 'react-native-unistyles';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Table } from '@/components/General/Table';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import { ActivityIndicator } from 'react-native-paper';
import SolidButton from '@/components/General/SolidButton';
import FormEntryFiles from '@/components/Forms/FormEntryFiles';
import Chip from '@/components/General/Chip';
import Toggle from '@/components/General/inputs/Toggle';
import { paymentStatusLabels } from '@/data/formPayments';
import Avatar from '@/components/People/Avatar';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import { dropdownIcons } from '@/components/General/DropdownMenu';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { exportEntriesCSV, deleteFormEntry, updateEntryLink } from '@/data/forms';
import { enrollPerson } from '@/data/paths';
import { startFollowUpPath } from '@/data/followUp';
import { buildFollowUpPathOptions } from '@/utils/automationBuilder';
import SheetModal from '@/components/General/SheetModal';
import { showAlert } from '@/utils/showAlert';
import { flattenFields } from '@/utils/formsSchema';
import { getPeopleByIds } from '@/data/people';
import usePeopleBatchActions from '@/utils/hooks/usePeopleBatchActions';
import { signatureSummary } from '@/utils/formSignaturePolicy';

const formatDate = (val) => new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
const formatDateTime = (val) => new Date(val).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
const formatTime = (val) => new Date(val).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

const ENROLLMENT_STATUS_CONFIG = {
    ACTIVE: { label: 'Active', colorVariant: 'primary' },
    COMPLETED: { label: 'Completed', colorVariant: 'success' },
    CANCELLED: { label: 'Cancelled', colorVariant: 'error' },
    PAUSED: { label: 'Paused', colorVariant: 'warning' },
};

const getEnrollmentStepCount = (enrollment) => {
    if (!enrollment) return null;
    const totalSteps = (enrollment.Path?.Steps || []).filter(s => s.stepType !== 'TRIGGER').length;
    const completedSteps = (enrollment.EnrollmentSteps || []).filter(s =>
        (s.status === 'COMPLETED' || s.status === 'SKIPPED') && s.PathStep?.stepType !== 'TRIGGER'
    ).length;
    return { completedSteps, totalSteps };
};

/**
 * Entries tab — lists submissions in a Table with columns derived from the
 * form schema. CSV export in the header. Row tap navigates to the entry detail.
 * Batch-select + delete for manage-permission users. Gated by `canManageEntries`.
 */
const FormEntriesTab = ({ form, formID, canManageEntries, router }) => {
    const { theme: { colors, fonts } } = useUnistyles();
    const queryClient = useQueryClient();
    const [sortConfig, setSortConfig] = useState(null);
    const [showArchived, setShowArchived] = useState(false);
    const [linkPickerOpen, setLinkPickerOpen] = useState(false);
    const [linkingEntryId, setLinkingEntryId] = useState(null);
    const { data: entries, isFetching } = Query.formEntries.byFormId({ formID });
    const { data: loggedInUser } = Query.people.loggedInUser();

    // Start Follow Up Path (§14.5/R08): distinct from the triggerPath
    // automation binding — pickers list FollowUpPaths, never legacy Paths.
    const canStartFollowUp = !!loggedInUser?.can?.('View Follow Up');
    const { data: followUpPaths } = Query.followUp.paths({
        organizationID: loggedInUser?.organizationID,
        options: { enabled: canStartFollowUp && !!loggedInUser?.organizationID },
    });
    const followUpPathOptions = useMemo(() => buildFollowUpPathOptions(followUpPaths), [followUpPaths]);
    const [followUpPickerEntry, setFollowUpPickerEntry] = useState(null);
    const followUpMutation = useMutation({
        mutationFn: ({ followUpPathID, personID }) => startFollowUpPath({
            personID,
            pathID: followUpPathID,
            idempotencyKey: `form-entry:${formID}:${personID}:${followUpPathID}`,
        }),
        onSuccess: (result) => {
            setFollowUpPickerEntry(null);
            if (result?.enrollmentID) {
                router.push(`/follow-up/enrollments/${result.enrollmentID}`);
            }
        },
    });

    const triggerPathId = form?.actions?.triggerPath?.enabled ? form.actions.triggerPath.pathId : null;

    const enrollMutation = useMutation({
        mutationFn: ({ pathID, personID, formEntryID }) => enrollPerson({
            pathID,
            personID,
            enrolledBy: loggedInUser?.id,
            triggerContext: { triggerType: 'FORM_ENTRY', formEntryID },
        }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.formEntries.byFormId(formID) });
            queryClient.invalidateQueries({ queryKey: QueryKeys.paths.all() });
        },
    });

    const fields = useMemo(() => flattenFields(form?.schema), [form?.schema]);
    const visibleEntries = useMemo(() => (entries || []).filter((entry) => showArchived || !entry.archivedAt), [entries, showArchived]);

    const deleteEntryMutation = useMutation({
        mutationFn: (entryIDs) => {
            const ids = Array.isArray(entryIDs) ? entryIDs : [entryIDs];
            return Promise.all(ids.map((id) => deleteFormEntry({ entryID: id })));
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.formEntries.byFormId(formID) });
        },
    });

    const linkMutation = useMutation({
        mutationFn: ({ entryID, personID }) => updateEntryLink({ entryID, personID }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.formEntries.byFormId(formID) });
        },
    });

    const handleLinkSelect = useCallback((selected) => {
        if (selected?.id && linkingEntryId) {
            linkMutation.mutate({ entryID: linkingEntryId, personID: selected.id });
        }
        setLinkPickerOpen(false);
        setLinkingEntryId(null);
    }, [linkingEntryId, linkMutation]);

    const openLinkPicker = useCallback((entryId) => {
        setLinkingEntryId(entryId);
        setLinkPickerOpen(true);
    }, []);

    const columns = useMemo(() => {
        const cols = [
            {
                accessor: 'createdAt',
                value: 'Submitted',
                sortable: true,
                renderer: (e) => e.createdAt ? formatDateTime(e.createdAt) : '—',
            },
            {
                accessor: 'linkedPerson',
                value: 'Linked Person',
                sortable: true,
                renderer: (entry) => {
                    if (entry.LinkedPerson) {
                        return (
                            <Pressable
                                onPress={() => router.push('/people/' + entry.LinkedPerson.id)}
                                style={styles.linkedCell}
                            >
                                <Avatar profile={entry.LinkedPerson} size={28} disableBorder />
                                <Text style={styles.linkedName(colors, fonts)}>
                                    {entry.LinkedPerson.firstName || ''} {entry.LinkedPerson.lastName || ''}
                                </Text>
                            </Pressable>
                        );
                    }
                    if (canManageEntries) {
                        return (
                            <SolidButton
                                text="Link"
                                onPress={() => openLinkPicker(entry.id)}
                                variant="muted"
                                size="sm"
                                type="textSecondary"
                                testID={`entry-link-btn-${entry.id}`}
                            />
                        );
                    }
                    return entry.linkAmbiguous ? 'Ambiguous' : '—';
                },
            },
            {
                accessor: 'pathEnrollment',
                value: 'Automation',
                sortable: false,
                renderer: (entry) => {
                    if (!entry.PathEnrollment) {
                        if (!triggerPathId || !entry.LinkedPerson || (entry.paymentSnapshot && !['PAID', 'NOT_REQUIRED'].includes(entry.paymentStatus))) return '—';
                        return (
                            <SolidButton
                                text="Start Run"
                                onPress={() => enrollMutation.mutate({ pathID: triggerPathId, personID: entry.LinkedPerson.id, formEntryID: entry.id })}
                                processing={enrollMutation.isPending}
                                variant="muted"
                                size="sm"
                                type="primary"
                                iconName='arrow-forward'
                                testID={`entry-enroll-btn-${entry.id}`}
                            />
                        );
                    }
                    const en = entry.PathEnrollment;
                    const statusConfig = ENROLLMENT_STATUS_CONFIG[en.status] || ENROLLMENT_STATUS_CONFIG.ACTIVE;
                    const stepCount = getEnrollmentStepCount(en);
                    return (
                        <View style={styles.pathCell}>
                            <Pressable
                                onPress={() => router.push(`/automations/enrollment/${en.id}?pathID=${en.pathID}`)}
                                style={styles.pathCell}
                                testID={`entry-view-path-${entry.id}`}
                            >
                                <Chip colorVariant={statusConfig.colorVariant}>{statusConfig.label}</Chip>
                                {stepCount && (
                                    <Text style={styles.pathStepCount(colors, fonts)}>
                                        {stepCount.completedSteps}/{stepCount.totalSteps} steps
                                    </Text>
                                )}
                            </Pressable>
                            {canStartFollowUp && entry.LinkedPerson && followUpPathOptions.length > 0 && (
                                <Pressable
                                    onPress={() => setFollowUpPickerEntry(entry)}
                                    testID={`entry-start-follow-up-${entry.id}`}
                                >
                                    <Text style={styles.followUpLink(colors, fonts)}>+ Follow Up Path</Text>
                                </Pressable>
                            )}
                        </View>
                    );
                },
            },
        ];
        if (fields.some((field) => field.type === 'payment') || entries?.some((entry) => entry.paymentSnapshot)) cols.splice(1, 0, {
            accessor: 'paymentStatus', value: 'Payment', sortable: true,
            renderer: (entry) => <View><Chip colorVariant={entry.paymentStatus === 'PAID' ? 'success' : entry.paymentStatus === 'PENDING' ? 'warning' : 'forms'}>{paymentStatusLabels[entry.paymentStatus] || 'Not Required'}</Chip>{entry.archivedAt && <Text style={styles.linkedName(colors, fonts)}>Archived</Text>}</View>,
        });
        for (const field of fields.filter((field) => field.type !== 'payment')) {
            const sortable = field.type !== 'checkbox' && field.type !== 'checkboxGroup' && field.type !== 'file';
            cols.push({
                accessor: `payload.${field.fieldId}`,
                value: field.label || field.fieldId,
                sortable,
                renderer: (entry) => {
                    if (field.type !== 'file') return renderCellValue(entry.payload?.[field.fieldId], field);
                    const section = form?.schema?.sections?.find(section => section.fields?.some(item => item.fieldId === field.fieldId));
                    const values = [entry.payload?.[field.fieldId], ...(entry.payload?.__repeats__?.[section?.id] || []).map(instance => instance?.[field.fieldId])].flat().filter(Boolean);
                    return <FormEntryFiles value={values} testID={`entry-table-file-${entry.id}-${field.fieldId}`} emptyText="—" />;
                },
            });
        }
        return cols;
    }, [fields, entries, canManageEntries, colors, fonts, router, enrollMutation, triggerPathId, form?.schema, openLinkPicker, canStartFollowUp, followUpPathOptions]);

    // Client-side sort
    const sortedEntries = useMemo(() => {
        if (!sortConfig) return visibleEntries;
        const { key, direction } = sortConfig;
        return [...visibleEntries].sort((a, b) => {
            let valA, valB;
            if (key === 'createdAt') {
                valA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                valB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                return direction === 'asc' ? valA - valB : valB - valA;
            }
            if (key === 'paymentStatus') {
                valA = a.paymentStatus || 'NOT_REQUIRED';
                valB = b.paymentStatus || 'NOT_REQUIRED';
            } else if (key === 'linkedPerson') {
                valA = a.LinkedPerson ? `${a.LinkedPerson.firstName || ''} ${a.LinkedPerson.lastName || ''}`.toLowerCase() : '';
                valB = b.LinkedPerson ? `${b.LinkedPerson.firstName || ''} ${b.LinkedPerson.lastName || ''}`.toLowerCase() : '';
            } else {
                // payload.<fieldId> accessor
                const fieldId = key.replace('payload.', '');
                const field = fields.find((f) => f.fieldId === fieldId);
                valA = getSortValue(a.payload?.[fieldId], field);
                valB = getSortValue(b.payload?.[fieldId], field);
                if (field?.type === 'number') {
                    return direction === 'asc' ? valA - valB : valB - valA;
                }
            }
            valA = String(valA ?? '').toLowerCase();
            valB = String(valB ?? '').toLowerCase();
            if (valA < valB) return direction === 'asc' ? -1 : 1;
            if (valA > valB) return direction === 'asc' ? 1 : -1;
            return 0;
        });
    }, [visibleEntries, sortConfig, fields]);

    // Resolve selected entries to person IDs (via LinkedPerson).
    // Handles both 'include' (specific items) and 'exclude' (select-all-minus-deselected) modes.
    const getSelectedPersonIds = async (mode, ids) => {
        const idSet = new Set(ids);
        const selected = (entries || []).filter(e =>
            mode === 'exclude' ? !idSet.has(e.id) : idSet.has(e.id)
        );
        const personIds = selected.map(e => e.LinkedPerson?.id).filter(Boolean);
        return [...new Set(personIds)];
    };

    // Resolve selected entries to full person objects (fetch from DB for email/phones)
    const getSelectedPeople = async (mode, ids) => {
        const personIds = await getSelectedPersonIds(mode, ids);
        if (personIds.length === 0) return [];
        return getPeopleByIds(personIds);
    };

    const { peopleBatchActions: sharedPeopleActions, modalElements: peopleBatchModals } = usePeopleBatchActions({
        getSelectedPersonIds,
        getSelectedPeople,
    });

    const batchActions = useMemo(() => {
        if (!canManageEntries) return [];
        return [
            ...sharedPeopleActions,
            {
                name: 'Archive / Delete',
                icon: dropdownIcons.delete,
                destructive: true,
                onSelect: (_mode, ids, selectedCount) => {
                    showAlert({
                        title: 'Archive / Delete Entries',
                        subtitle: `Remove ${selectedCount} selected ${selectedCount === 1 ? 'entry' : 'entries'}? Entries with payment history are archived; others are permanently deleted.`,
                        isDestructive: true,
                        confirmText: 'Delete',
                        onConfirm: () => deleteEntryMutation.mutate(Array.from(ids)),
                    });
                },
            },
        ];
    }, [canManageEntries, deleteEntryMutation, sharedPeopleActions]);

    const handleExport = useCallback(() => {
        exportEntriesCSV({ formID });
    }, [formID]);

    const handleRowClick = useCallback((entry) => {
        router.push(`/forms/entries/${entry.id}`);
    }, [router]);

    if (!canManageEntries) {
        return (
            <View style={styles.emptyContainer}>
                <Text style={styles.emptyText(colors, fonts)}>You don&apos;t have permission to manage entries for this form.</Text>
            </View>
        );
    }

    if (isFetching && !entries) {
        return (
            <View style={styles.emptyContainer}>
                <ActivityIndicator size="large" />
            </View>
        );
    }

    return (
        <View style={styles.container} testID="form-entries-tab">
            <View style={styles.header}>
                <Text style={styles.countText(colors, fonts)}>{visibleEntries.length} entries</Text>
                <SolidButton
                    text="Export CSV"
                    onPress={handleExport}
                    variant="outlined"
                    iconName="download-outline"
                    iconBefore
                    size="sm"
                    testID="form-export-csv"
                />
            </View>

            <View style={styles.archivedToggleRow}>
                <Text style={styles.archivedToggleLabel(colors, fonts)}>Show archived entries</Text>
                <Toggle
                    accessibilityLabel="Show archived entries"
                    value={showArchived}
                    onValueChange={setShowArchived}
                    testID="form-entries-show-archived"
                />
            </View>
            {visibleEntries.length === 0 ? (
                <View style={styles.emptyContainer}>
                    <EmptyListPlaceholder
                        spacing={10}
                        message="No entries yet. Published forms will collect submissions here."
                    />
                </View>
            ) : (
                <View style={styles.tableWrap} testID="form-entries-table">
                    <Table
                        data={sortedEntries}
                        columns={columns}
                        onRowClick={handleRowClick}
                        isLoading={isFetching}
                        sortConfig={sortConfig}
                        onSortChange={setSortConfig}
                        batchSelection={canManageEntries}
                        batchActions={batchActions}
                        contentContainerStyle={{ paddingBottom: 100 }}
                    />
                </View>
            )}
            {peopleBatchModals}
            {/* Start Follow Up Path picker — FollowUpPaths only; person is
                the entry's resolved LinkedPerson (§14.5). */}
            <SheetModal
                isOpen={!!followUpPickerEntry}
                onCancel={() => setFollowUpPickerEntry(null)}
                onClose={() => setFollowUpPickerEntry(null)}
                title="Start Follow Up Path"
                cancelText="Cancel"
            >
                <View style={{ gap: 8 }}>
                    <Text style={styles.followUpPickerHint(colors, fonts)}>
                        Starts a Follow Up journey for {followUpPickerEntry?.LinkedPerson?.firstName || 'this person'} — separate from automation runs.
                    </Text>
                    {followUpPathOptions.map(opt => (
                        <Pressable
                            key={opt.value}
                            style={styles.followUpOption(colors)}
                            disabled={followUpMutation.isPending}
                            onPress={() => followUpMutation.mutate({ followUpPathID: opt.value, personID: followUpPickerEntry?.LinkedPerson?.id })}
                            testID={`entry-follow-up-path-${opt.value}`}
                        >
                            <Text style={styles.followUpOptionName(colors, fonts)}>{opt.label}</Text>
                        </Pressable>
                    ))}
                </View>
            </SheetModal>
            {linkPickerOpen && (
                <SheetSelector
                    title="Link to Person"
                    open={linkPickerOpen}
                    onOpen={() => setLinkPickerOpen(true)}
                    onClose={() => { setLinkPickerOpen(false); setLinkingEntryId(null); }}
                    onCancel={() => { setLinkPickerOpen(false); setLinkingEntryId(null); }}
                    type="people"
                    onChange={handleLinkSelect}
                />
            )}
        </View>
    );
};

/**
 * Compact identity for a profile cell — resolved person name when RLS permits,
 * otherwise a neutral fallback. Never the full profile-card payload.
 */
const ProfileCell = ({ personID }) => {
    const { data: person, isLoading } = Query.people.basicById({ id: personID });
    if (isLoading) return <Text>…</Text>;
    const name = person ? `${person.firstName || ''} ${person.lastName || ''}`.trim() : '';
    return <Text>{name || 'Profile selected'}</Text>;
};

const getSortValue = (value, field) => {
    if (value == null) return '';
    switch (field?.type) {
        case 'name':
            return `${value.first || ''} ${value.last || ''}`.trim();
        case 'number':
            return Number(value) || 0;
        case 'date':
        case 'datetime':
            return value ? new Date(value).getTime() : 0;
        case 'time': {
            // Time values are stored as ISO datetimes; the date part is ignored,
            // so sort by time-of-day instead of the full timestamp.
            const d = new Date(value);
            return Number.isNaN(d.getTime()) ? 0 : d.getHours() * 3600 + d.getMinutes() * 60 + d.getSeconds();
        }
        case 'address':
            return [value.street1, value.city, value.state, value.zip].filter(Boolean).join(', ');
        case 'signature':
            // Sort by signer name — never stringify the evidence object.
            return value?.signerName || '';
        case 'profile':
            return value?.personID || '';
        default:
            return typeof value === 'object' ? JSON.stringify(value) : String(value);
    }
};

const renderCellValue = (value, field) => {
    if (value == null) return '—';
    switch (field.type) {
        case 'name':
            return `${value.first || ''} ${value.last || ''}`.trim() || '—';
        case 'address':
            return [value.street1, value.city, value.state, value.zip].filter(Boolean).join(', ') || '—';
        case 'checkbox':
            return value ? 'Yes' : 'No';
        case 'checkboxGroup':
            return Array.isArray(value) ? value.join(', ') : String(value);
        case 'date':
            return formatDate(value);
        case 'datetime':
            return formatDateTime(value);
        case 'time':
            return formatTime(value);
        case 'file':
            if (Array.isArray(value)) return value.map((f) => f?.filename || f?.path || '').join(', ');
            return value?.filename || value?.path || '—';
        case 'signature':
            // Compact summary only — no image render per row.
            return signatureSummary(value) || '—';
        case 'profile':
            return <ProfileCell personID={value?.personID} />;
        default:
            return typeof value === 'object' ? JSON.stringify(value) : String(value);
    }
};

const styles = StyleSheet.create((_theme) => ({
    container: { flex: 1, width: '100%', alignSelf: 'center' },
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 16,
        paddingTop: 12,
        paddingBottom: 8,
    },
    countText: (colors, fonts) => ({
        fontSize: 13,
        fontFamily: fonts.bold,
        color: colors.textSecondary,
    }),
    archivedToggleRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 8,
        paddingHorizontal: 16,
        paddingVertical: 6,
    },
    archivedToggleLabel: (colors, fonts) => ({
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    }),
    tableWrap: { flex: 1, paddingHorizontal: 8 },
    emptyContainer: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 40 },
    emptyText: (colors, fonts) => ({
        fontSize: 15,
        fontFamily: fonts.heading,
        color: colors.textSecondary,
        textAlign: 'center',
    }),
    linkedCell: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
    },
    linkedName: (colors, fonts) => ({
        fontSize: 13,
        fontFamily: fonts.heading,
        color: colors.text,
    }),
    pathCell: {
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 4,
    },
    pathStepCount: (colors, fonts) => ({
        fontSize: 12,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    }),
    followUpLink: (colors, fonts) => ({
        fontSize: 12,
        fontFamily: fonts.bold,
        color: colors.primary,
    }),
    followUpPickerHint: (colors, fonts) => ({
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        lineHeight: 19,
        marginBottom: 6,
    }),
    followUpOption: (colors) => ({
        backgroundColor: colors.surface,
        borderRadius: 10,
        padding: 14,
        borderWidth: 1,
        borderColor: colors.border,
    }),
    followUpOptionName: (colors, fonts) => ({
        fontSize: 14,
        fontFamily: fonts.bold,
        color: colors.text,
    }),
}));

export default FormEntriesTab;
